import { EntityContextType, useEntity } from 'components';
import type { Income } from 'modules/invoicing/income/types';
import type { Expense } from 'modules/invoicing/expenses/types';
import type { CampaignDetails } from 'modules/campaign/campaign/types';
import { FormContainer } from './FormContainer';
import { useBuilder } from './useBuilder';
import { useLinker } from './useLinker';
import { CampaignBuilder } from './CampaignBuilder';
import { NetLinker } from './NetLinker';
import { BonusLinker } from './BonusLinker';
import { ImportedBlocksLinker } from './ImportedBlocksLinker';
import type { AVB } from 'modules/guarantee/types';
export { CampaignCorrector as CampaignInvoiceCorrector } from './CampaignCorrector';

import { InvoiceFields } from './InvoiceFields';
import { LinksProvider } from './LinksProvider';
import { LinkerFields } from './LinkerFields';
import { PortalAnchor, PortalProvider } from 'components/Portal';
import invariant from 'tiny-invariant';

export function AVBIncomeBuilder(): React.ReactElement {
  const { endpoint } = useEntity<AVB>();
  const handler = useBuilder(endpoint);
  const { disabled, url } = handler;
  return (
    <FormContainer title="Create" {...handler}>
      <PortalProvider>
        <InvoiceFields hideServicePeriod />
        <div className="form-row">
          <div className="col-md-6 d-flex align-items-start">
            <LinkerFields className="flex-grow-1" />
          </div>
          {/* controls and summary */}
          <PortalAnchor className="d-flex align-items-start flex-grow-1" />
        </div>
        <LinksProvider url={url} shouldHaveFiancePeriod>
          {(ctx) => <BonusLinker {...ctx} direction="income" disabled={disabled} url={url} />}
        </LinksProvider>
      </PortalProvider>
    </FormContainer>
  );
}

function AVBIncomeLinker({ ctx: { endpoint, item, setItem } }: { ctx: EntityContextType<Income> }): React.ReactElement {
  invariant(item.source_avb);

  const handler = useLinker(endpoint, item, setItem);
  const { disabled, url } = handler;

  return (
    <FormContainer title="Link" {...handler}>
      <PortalProvider>
        <div className="form-row">
          <div className="col-md-6 d-flex align-items-start">
            <LinkerFields className="flex-grow-1" />
          </div>
          {/* controls and summary */}
          <PortalAnchor className="d-flex align-items-start flex-grow-1" />
        </div>
        <LinksProvider url={url} shouldHaveFiancePeriod>
          {(ctx) => <BonusLinker {...ctx} direction="income" invoice={item} disabled={disabled} url={url} />}
        </LinksProvider>
      </PortalProvider>
    </FormContainer>
  );
}

function CampaignIncomeLinker({
  ctx: { endpoint, item, setItem },
}: {
  ctx: EntityContextType<Income>;
}): React.ReactElement {
  const handler = useLinker(endpoint, item, setItem);
  const { disabled, url } = handler;

  invariant(!item.source_avb);

  return (
    <FormContainer title="Link" {...handler}>
      <LinksProvider url={url} shouldHaveFiancePeriod={false}>
        {(ctx) => <NetLinker invoice={item} {...ctx} direction="income" disabled={disabled} />}
      </LinksProvider>
    </FormContainer>
  );
}

export function CampaignIncomeBuilder(): React.ReactElement {
  const { endpoint } = useEntity<CampaignDetails>();
  const handler = useBuilder(endpoint);
  const { url } = handler;

  return (
    <FormContainer title="Link" {...handler}>
      <LinksProvider url={url} shouldHaveFiancePeriod={false}>
        {(ctx) => <CampaignBuilder {...ctx} />}
      </LinksProvider>
    </FormContainer>
  );
}

function CVBExpenseLinker({
  ctx: { endpoint, item, setItem },
}: {
  ctx: EntityContextType<Expense>;
}): React.ReactElement {
  invariant(item.source_cvb);

  const handler = useLinker(endpoint, item, setItem);
  const { disabled, url } = handler;

  return (
    <FormContainer title="Link" {...handler}>
      <PortalProvider>
        <div className="form-row">
          <div className="col-md-6 d-flex align-items-start">
            <LinkerFields className="flex-grow-1" />
          </div>
          {/* controls and summary */}
          <PortalAnchor className="d-flex align-items-start flex-grow-1" />
        </div>
        <LinksProvider url={url} shouldHaveFiancePeriod>
          {(ctx) => <BonusLinker {...ctx} direction="expense" invoice={item} disabled={disabled} url={url} />}
        </LinksProvider>
      </PortalProvider>
    </FormContainer>
  );
}

function CampaignExpenseLinker({
  ctx: { endpoint, item, setItem },
}: {
  ctx: EntityContextType<Expense>;
}): React.ReactElement {
  const handler = useLinker(endpoint, item, setItem);
  const { disabled, url } = handler;

  invariant(!item.source_cvb);

  return (
    <FormContainer title="Link" {...handler}>
      <LinksProvider url={url} shouldHaveFiancePeriod={false}>
        {(ctx) => (
          <NetLinker
            invoice={item}
            {...ctx}
            direction="expense"
            disabled={disabled}
            renderCandidatesTop={() => <ImportedBlocksLinker expense={item} linkerContext={ctx} />}
          />
        )}
      </LinksProvider>
    </FormContainer>
  );
}

export function IncomeLinker(): React.ReactElement {
  const ctx = useEntity<Income>();

  if (ctx.item.source_avb) {
    return <AVBIncomeLinker ctx={ctx} />;
  }
  return <CampaignIncomeLinker ctx={ctx} />;
}

export function ExpenseLinker(): React.ReactElement {
  const ctx = useEntity<Expense>();
  if (ctx.item.source_cvb) {
    return <CVBExpenseLinker ctx={ctx} />;
  }
  return <CampaignExpenseLinker ctx={ctx} />;
}
